<template>
  <div class="front-layout">
    <!-- 顶部导航 -->
    <nav class="site-nav" :class="{ scrolled: isScrolled }">
      <div class="nav-inner">
        <router-link to="/" class="nav-brand">
          <img v-if="siteSettings.logo" :src="baseUrl + siteSettings.logo" :title="siteSettings.siteName" class="nav-logo" loading="lazy" />
          <img v-else src="@/assets/qianfanLogo.png" title="千帆学堂" class="nav-logo" />
          <span class="nav-title">{{ siteSettings.siteName }}</span>
        </router-link>
        <div class="nav-links" :class="{ open: mobileMenuOpen }">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="nav-link"
            :class="{ active: $route.path === item.path || (item.path !== '/' && $route.path.startsWith(item.path)) }"
            @click.native="mobileMenuOpen = false"
          >{{ item.label }}</router-link>
          <router-link to="/contact" class="nav-link nav-link--cta" @click.native="mobileMenuOpen = false">立即咨询</router-link>
        </div>
        <div class="nav-right">
          <span class="nav-phone">
            <i class="el-icon-phone-outline"></i>
            {{ siteSettings.contactPhone }}
          </span>
          <router-link to="/contact" class="nav-cta">立即咨询</router-link>
        </div>
        <button class="hamburger" @click="mobileMenuOpen = !mobileMenuOpen">
          <span></span><span></span><span></span>
        </button>
      </div>
    </nav>

    <!-- 移动端菜单覆盖层（独立于nav，避免backdrop-filter影响fixed定位） -->
    <div class="mobile-menu-overlay" :class="{ open: mobileMenuOpen }" @click="mobileMenuOpen = false">
      <div class="mobile-menu-inner" @click.stop>
        <router-link
          v-for="item in navItems"
          :key="'m-' + item.path"
          :to="item.path"
          class="mobile-menu-link"
          :class="{ active: $route.path === item.path || (item.path !== '/' && $route.path.startsWith(item.path)) }"
          @click.native="mobileMenuOpen = false"
        >{{ item.label }}</router-link>
        <router-link to="/contact" class="mobile-menu-link mobile-menu-cta" @click.native="mobileMenuOpen = false">立即咨询</router-link>
      </div>
    </div>

    <!-- 页面内容 -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- 页脚 -->
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="footer-logo">
            <img v-if="siteSettings.logo" :src="baseUrl + siteSettings.logo" :alt="siteSettings.siteName" class="footer-logo-icon" loading="lazy" />
            <img v-else src="@/assets/qianfanLogo.png" alt="千帆学堂" class="footer-logo-icon" loading="lazy" />
            <span>{{ siteSettings.siteName }}</span>
          </div>
          <p class="footer-desc">{{ siteSettings.siteDescription }}</p>
          <div class="footer-contact">
            <p><i class="el-icon-phone"></i> {{ siteSettings.contactPhone }}{{ siteSettings.contactWechat ? '（微信：' + siteSettings.contactWechat + '）' : '' }}</p>
            <p><i class="el-icon-message"></i> {{ siteSettings.contactEmail }}</p>
          </div>
        </div>
        <div class="footer-col">
          <h4>快速导航</h4>
          <ul>
            <li><router-link to="/">首页</router-link></li>
            <li><router-link to="/about">公司介绍</router-link></li>
            <li><router-link to="/products">产品服务</router-link></li>
            <li><router-link to="/honors">荣誉展示</router-link></li>
          </ul>
        </div>
        <div class="footer-col">
          <h4>更多内容</h4>
          <ul>
            <li><router-link to="/news">新闻动态</router-link></li>
            <li><router-link to="/qna">一问一答</router-link></li>
            <li><router-link to="/contact">联系我们</router-link></li>
          </ul>
        </div>
        <div class="footer-col">
          <h4>联系我们</h4>
          <ul>
            <li>{{ siteSettings.contactAddress }}</li>
            <li><router-link to="/contact">在线咨询</router-link></li>
          </ul>
        </div>
      </div>

      <!-- 社交媒体二维码 -->
      <div class="footer-social" v-if="siteSettings.wechatQrcode || siteSettings.wechatVideoQrcode || siteSettings.douyinQrcode">
        <div class="footer-social__inner">
          <div class="footer-social__item" v-if="siteSettings.wechatQrcode">
            <img :src="baseUrl + siteSettings.wechatQrcode" alt="微信公众号" loading="lazy" />
            <span>微信公众号</span>
          </div>
          <div class="footer-social__item" v-if="siteSettings.wechatVideoQrcode">
            <img :src="baseUrl + siteSettings.wechatVideoQrcode" alt="微信视频号" loading="lazy" />
            <span>微信视频号</span>
          </div>
          <div class="footer-social__item" v-if="siteSettings.douyinQrcode">
            <img :src="baseUrl + siteSettings.douyinQrcode" alt="抖音" loading="lazy" />
            <span>抖音</span>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <a href="https://beian.miit.gov.cn/" target="_blank" style="text-decoration:none;">赣ICP备2026004193号-1</a>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=36079002000619" target="_blank" rel="noreferrer noopener" style="color:#666; text-decoration:none;">
          赣公网安备36079002000619号
        </a>
        <span>Copyright © 2021-{{ currentYear || '2026' }} 赣州千帆学堂教育咨询有限公司 版权所有</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { getSiteSettings } from "@/api/settings"
import config from '@/config'
import { recordVisit } from "@/api/home";
export default {
  name: 'FrontLayout',
  data() {
    return {
      currentYear: '2026',
      isScrolled: false,
      mobileMenuOpen: false,
      baseUrl: config.baseApi,
      siteSettings: {
        siteName: '',
        siteDescription: '',
        siteKeywords: '',
        logo: '',
        contactPhone: '',
        contactEmail: '',
        contactWechat: '',
        contactAddress: '',
        contactPerson: '',
        contactPhoneHint: '',
        contactEmailHint: '',
        contactAddressHint: '',
        contactWorkTime: '',
        contactWorkTimeHint: '',
        wechatQrcode: '',
        wechatVideoQrcode: '',
        douyinQrcode: '',
        seo: {
          title: '',
          description: '',
          keywords: ''
        }
      },
      navItems: [
        { path: '/', label: '首页' },
        { path: '/about', label: '公司介绍' },
        { path: '/products', label: '产品服务' },
        { path: '/honors', label: '荣誉展示' },
        { path: '/news', label: '新闻动态' },
        { path: '/qna', label: '一问一答' },
        { path: '/contact', label: '联系我们' }
      ]
    }
  },
  created() {
    recordVisit()
    this.currentYear = new Date().getFullYear() + ''
    this.updateSEOMeta()
    this.fetchSettings()
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async fetchSettings() {
      try {
        const res = await getSiteSettings()
        if (res && res.data && res.data.jsonContent && res.data.jsonContent.siteName) {
          this.siteSettings = res.data.jsonContent
          this.updateSEOMeta()
        }
      } catch (e) {
        console.error('获取系统设置失败', e)
      }
    },
    updateSEOMeta() {
      const seo = this.siteSettings.seo
      if (seo) {
        if (seo.title) document.title = seo.title
        let metaKeywords = document.querySelector('meta[name="keywords"]')
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta')
          metaKeywords.setAttribute('name', 'keywords')
          document.head.appendChild(metaKeywords)
        }
        if (seo.keywords) metaKeywords.setAttribute('content', seo.keywords)
        let metaDesc = document.querySelector('meta[name="description"]')
        if (!metaDesc) {
          metaDesc = document.createElement('meta')
          metaDesc.setAttribute('name', 'description')
          document.head.appendChild(metaDesc)
        }
        if (seo.description) metaDesc.setAttribute('content', seo.description)
      }
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/global.less';

.site-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid @seed-border;
  height: @seed-nav-h;
  transition: box-shadow 0.3s;

  &.scrolled {
    box-shadow: 0 1px 8px @seed-shadow-sm;
  }
}

.nav-inner {
  max-width: @seed-container;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  height: 100%;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.nav-logo {
  height: 70px;
  width: auto;
  object-fit: contain;
  display: block;
}

.nav-title {
  font-size: 36px;
  font-weight: 600;
  letter-spacing: -0.01em;
  font-family: @seed-font-display;
  color: @seed-fg;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 48px;
  flex: 1;
}

.nav-link {
  padding: 8px 16px;
  font-size: 18px;
  font-weight: 500;
  color: @seed-muted;
  border-radius: 6px;
  transition: color 0.2s, background 0.2s;
  letter-spacing: 0.02em;

  &:hover, &.active {
    color: @seed-fg;
    background: @seed-section;
  }
  &.active {
    color: @seed-primary;
  }
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.nav-phone {
  font-size: 18px;
  color: @seed-muted;
  display: flex;
  align-items: center;
  gap: 6px;

  i { color: @seed-primary; }
}

.nav-cta {
  background: @seed-primary;
  color: #fff;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 500;
  transition: background 0.2s, transform 0.15s;
  letter-spacing: 0.02em;

  &:hover {
    background: @seed-primary-hover;
    transform: translateY(-1px);
  }
}

.hamburger {
  display: none;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 8px;

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: @seed-fg;
    margin: 5px 0;
    transition: 0.3s;
  }
}

.main-content {
  min-height: 100vh;
}

// 页脚
.site-footer {
  padding: 60px 0 32px;
  background: @seed-fg;
  color: #fff;
}

.footer-inner {
  max-width: @seed-container;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  span {
    font-size: 18px;
    font-weight: 600;
    color: #FFFFFF;
    font-family: @seed-font-display;
  }
}

.footer-logo-icon {
  height: 40px;
  width: auto;
  object-fit: contain;
  display: block;
}

.footer-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.7;
  margin-bottom: 20px;
  max-width: 320px;
}

.footer-contact {
  p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    i { font-size: 14px; }
  }
}

.footer-col {
  h4 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 20px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  ul {
    list-style: none;
    li {
      margin-bottom: 10px;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.5);

      a {
        transition: color 0.2s;
        &:hover { color: #fff; }
      }
    }
  }
}

// 社交媒体二维码
.footer-social {
  max-width: @seed-container;
  margin: 0 auto;
  padding: 0 24px;
  margin-top: 40px;

  &__inner {
    display: flex;
    justify-content: center;
    gap: 80px;
    padding-top: 32px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px 50px;

    img {
      width: 150px;
      height: 150px;
      border-radius: 8px;
      object-fit: cover;
      background: #fff;
      padding: 4px;
    }

    span {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
      letter-spacing: 0.02em;
    }
  }
}

.footer-bottom {
  max-width: @seed-container;
  margin: 0 auto;
  padding: 24px 24px 0;
  margin-top: 48px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
}

// 响应式
@media (max-width: @breakpoint-lg) {
  .footer-inner {
    grid-template-columns: 1fr 1fr;
  }
}

// 桌面端隐藏移动端CTA
.nav-link--cta {
  display: none;
}

// 移动端菜单覆盖层 - 桌面端隐藏
.mobile-menu-overlay {
  display: none;
}

@media (max-width: @breakpoint-md) {
  .nav-links { display: none; }

  .mobile-menu-overlay {
    display: none;
    position: fixed;
    top: @seed-nav-h;
    left: 0;
    width: 100vw;
    height: calc(100vh - @seed-nav-h);
    background: rgba(17, 24, 39, 0.97);
    z-index: 999;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    &.open {
      display: block;
    }
  }

  .mobile-menu-inner {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: 100%;
  }

  .mobile-menu-link {
    display: block;
    padding: 14px 16px;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.75);
    border-radius: @seed-radius;
    transition: color 0.2s, background 0.2s;

    &:hover, &.active {
      color: #fff;
      background: rgba(255, 255, 255, 0.1);
    }
    &.active {
      color: #fff;
      background: rgba(255, 255, 255, 0.15);
    }
  }

  .mobile-menu-cta {
    margin-top: 16px;
    background: @seed-primary;
    color: #fff;
    text-align: center;
    font-weight: 600;

    &:hover {
      background: @seed-primary-hover;
      color: #fff;
    }
  }

  .nav-right { display: none; }
  .hamburger {
    display: block;
    margin-left: auto;
  }

  .main-content {
    padding-top: @seed-nav-h;
  }

  .footer-inner {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .footer-social {
    margin-top: 24px;

    &__inner {
      gap: 24px;
      padding-top: 24px;
    }

    &__item img {
      width: 80px;
      height: 80px;
    }
  }

  .footer-bottom {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}

// 超小屏手机
@media (max-width: @breakpoint-sm) {
  .nav-inner {
    padding: 0 12px;
  }

  .brand-title {
    font-size: 16px;
  }

  .mobile-menu-inner {
    padding: 16px;
  }

  .mobile-menu-link {
    padding: 12px;
    font-size: 15px;
  }

  .footer {
    padding: 48px 0 0;
  }

  .footer-inner {
    gap: 24px;
  }

  .footer-brand {
    p {
      font-size: 13px;
    }
  }

  .footer-logo {
    img {
      height: 32px;
    }
  }

  .footer-links h4 {
    font-size: 14px;
    margin-bottom: 12px;
  }

  .footer-links a {
    font-size: 13px;
    padding: 4px 0;
  }

  .footer-contact p {
    font-size: 13px;
  }

  .footer-bottom {
    padding: 16px 0;
    font-size: 12px;
  }
}
</style>
